import React from "react";
import ReactDOM from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import * as ProcessActions from "../../redux/actions/process.actions";

const MODE_CFG = {
  actors:  { valueKey: "ACTORID",  labelKey: "ACTOR",    apiName: "set-actors-users",  title: "Assign Actors",  subtitle: "actors to this user" },
  users:   { valueKey: "USERID",   labelKey: "USERNAME", apiName: "set-actors-users",  title: "Assign Users",   subtitle: "users to this actor" },
  offices: { valueKey: "OFFICEID", labelKey: "OFFICE",   apiName: "set-offices-users", title: "Assign Offices", subtitle: "offices to this user" },
  usersO:  { valueKey: "USERID",   labelKey: "USERNAME", apiName: "set-offices-users", title: "Assign Users",   subtitle: "users to this office" },
};

function IconUsers() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4"/>
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  );
}

function IconOffice() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-6 9 6v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );
}

function IconClose() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M18 6L6 18M6 6l12 12"/>
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <circle cx="11" cy="11" r="7"/>
      <path d="M21 21l-4-4"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function useTheme() {
  const [isDark, setIsDark] = React.useState(
    () => document.documentElement.getAttribute("data-theme") === "dark"
  );
  React.useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.getAttribute("data-theme") === "dark")
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

function AssignPanel({ onClose, id, itemType, componentName, header, selectedRecord, rows, requesting, dispatch }) {
  const cfg = MODE_CFG[itemType] || MODE_CFG.actors;
  const isDark = useTheme();

  const t = {
    panel:         isDark ? "#1a2035" : "#fff",
    border:        isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb",
    borderLight:   isDark ? "rgba(255,255,255,0.06)" : "#f3f4f6",
    summaryBg:     isDark ? "#141e30" : "#f8fafc",
    text:          isDark ? "#e2e8f0" : "#111827",
    textMuted:     isDark ? "#94a3b8" : "#6b7280",
    textSub:       isDark ? "#64748b" : "#9ca3af",
    checkedBg:     isDark ? "rgba(59,130,246,0.12)" : "#eff6ff",
    checkedText:   isDark ? "#93c5fd" : "#1d4ed8",
    checkedBadgeBg:isDark ? "rgba(59,130,246,0.15)" : "#eff6ff",
    checkedBadgeTxt:isDark ? "#60a5fa" : "#0064d9",
    hoverBg:       isDark ? "rgba(255,255,255,0.04)" : "#f9fafb",
    avatar:        isDark ? "#2d3748" : "#e5e7eb",
    avatarText:    isDark ? "#94a3b8" : "#6b7280",
    checkedAvatar: isDark ? "rgba(59,130,246,0.25)" : "#bfdbfe",
    cbBorder:      isDark ? "#4b5563" : "#d1d5db",
    cbBg:          isDark ? "transparent" : "#fff",
    spinnerBorder: isDark ? "#2d3748" : "#e5e7eb",
    cancelBorder:  isDark ? "#4b5563" : "#d1d5db",
    cancelText:    isDark ? "#d1d5db" : "#374151",
    cancelBg:      isDark ? "transparent" : "#fff",
    footerBg:      isDark ? "#1a2035" : "#fff",
  };

  const initSelected = React.useMemo(() => {
    const sel = new Set();
    rows.forEach((r) => {
      if (r.isChecked === true) {
        sel.add(r[cfg.valueKey]);
      }
    });
    return sel;
  }, [rows, cfg.valueKey]);

  const [selected, setSelected] = React.useState(() => new Set(initSelected));
  const [searchQ, setSearchQ] = React.useState("");
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  React.useEffect(() => {
    setSelected(new Set(initSelected));
  }, [initSelected]);

  const entityLabel = React.useMemo(() => {
    if (!selectedRecord) return String(id);
    const keys = Object.keys(selectedRecord);
    return keys.length > 1 ? String(selectedRecord[keys[1]] || id) : String(id);
  }, [selectedRecord, id]);

  const filtered = rows.filter((r) => {
    const label = String(r[cfg.labelKey] || "");
    return label.toLowerCase().includes(searchQ.toLowerCase());
  });

  const toggle = (itemId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleSave = () => {
    dispatch(
      ProcessActions.processData(
        componentName,
        cfg.apiName,
        { id, type: itemType, items: [...selected] },
        header
      )
    );
    handleClose();
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 220);
  };

  const ModeIcon = itemType === "offices" ? IconOffice : IconUsers;
  const initial = String(entityLabel).charAt(0).toUpperCase();

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.38)",
          zIndex: 9998,
          opacity: visible ? 1 : 0,
          transition: "opacity 0.22s ease",
        }}
      />

      {/* Side panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "min(480px, 96vw)",
          height: "100vh",
          background: t.panel,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-6px 0 32px rgba(0,0,0,0.28)",
          fontFamily: '"72", "72full", Arial, Helvetica, sans-serif',
          transform: visible ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.22s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            background: "linear-gradient(135deg, #0d1117 0%, #1e1b4b 100%)",
            color: "#f1f5f9",
            padding: "20px 20px 16px",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  background: "rgba(255,255,255,0.18)",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ModeIcon />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>{cfg.title}</div>
                <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2, letterSpacing: 0.3 }}>
                  {cfg.subtitle}
                </div>
              </div>
            </div>

            <button
              onClick={handleClose}
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "none",
                borderRadius: 6,
                color: "#fff",
                cursor: "pointer",
                padding: "5px 6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
                flexShrink: 0,
              }}
              aria-label="Close"
            >
              <IconClose />
            </button>
          </div>

          {/* Entity being edited */}
          <div
            style={{
              marginTop: 14,
              background: "rgba(255,255,255,0.12)",
              borderRadius: 8,
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                background: "rgba(255,255,255,0.25)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {initial}
            </div>
            <div>
              <div style={{ fontSize: 10, opacity: 0.7, letterSpacing: 0.5, textTransform: "uppercase" }}>
                Editing
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.25 }}>{entityLabel}</div>
            </div>
          </div>

          {/* Search */}
          <div style={{ marginTop: 12, position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                opacity: 0.65,
                display: "flex",
                pointerEvents: "none",
              }}
            >
              <IconSearch />
            </span>
            <input
              type="text"
              placeholder="Search..."
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              autoComplete="off"
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "7px 12px 7px 30px",
                border: "1.5px solid rgba(255,255,255,0.35)",
                borderRadius: 7,
                fontSize: 13,
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                outline: "none",
              }}
              onFocus={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.7)"; }}
              onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.35)"; }}
            />
          </div>
        </div>

        {/* ── Summary bar ── */}
        <div
          style={{
            padding: "8px 20px",
            borderBottom: `1px solid ${t.border}`,
            background: t.summaryBg,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 12, color: t.textMuted }}>
            {filtered.length} available
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: selected.size > 0 ? t.checkedBadgeTxt : t.textSub,
              background: selected.size > 0 ? t.checkedBadgeBg : "transparent",
              padding: "2px 8px",
              borderRadius: 10,
            }}
          >
            {selected.size} selected
          </span>
        </div>

        {/* ── List ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "4px 0" }}>
          {requesting ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 160,
                gap: 12,
                color: "#9ca3af",
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  border: `3px solid ${t.spinnerBorder}`,
                  borderTopColor: "#0064d9",
                  borderRadius: "50%",
                  animation: "dlg-spin 0.7s linear infinite",
                }}
              />
              <span style={{ fontSize: 13, color: t.textSub }}>Loading...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 160,
                color: t.textSub,
                fontSize: 13,
              }}
            >
              {rows.length === 0 ? "No items available" : "No results for your search"}
            </div>
          ) : (
            filtered.map((item, i) => {
              const itemId = item[cfg.valueKey];
              const label = item[cfg.labelKey] || String(itemId);
              const isChecked = selected.has(itemId);
              const itemInitial = String(label).charAt(0).toUpperCase();
              return (
                <div
                  key={itemId != null ? itemId : i}
                  onClick={() => toggle(itemId)}
                  role="checkbox"
                  aria-checked={isChecked}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 20px",
                    cursor: "pointer",
                    background: isChecked ? t.checkedBg : "transparent",
                    borderBottom: `1px solid ${t.borderLight}`,
                    userSelect: "none",
                  }}
                  onMouseEnter={(e) => { if (!isChecked) e.currentTarget.style.background = t.hoverBg; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = isChecked ? t.checkedBg : "transparent"; }}
                >
                  {/* Custom checkbox */}
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      border: isChecked ? "2px solid #0064d9" : `2px solid ${t.cbBorder}`,
                      borderRadius: 4,
                      background: isChecked ? "#0064d9" : t.cbBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginRight: 14,
                      transition: "all 0.12s",
                    }}
                  >
                    {isChecked && <CheckIcon />}
                  </div>

                  {/* Avatar */}
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: isChecked ? t.checkedAvatar : t.avatar,
                      color: isChecked ? t.checkedText : t.avatarText,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                      marginRight: 12,
                      transition: "all 0.12s",
                    }}
                  >
                    {itemInitial}
                  </div>

                  {/* Label */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13.5,
                        color: isChecked ? t.checkedText : t.text,
                        fontWeight: isChecked ? 600 : 400,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        lineHeight: 1.35,
                      }}
                    >
                      {label}
                    </div>
                    {item.DESCRIPTION && (
                      <div
                        style={{
                          fontSize: 11.5,
                          color: t.textSub,
                          marginTop: 2,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          lineHeight: 1.3,
                        }}
                      >
                        {item.DESCRIPTION}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            padding: "14px 20px",
            borderTop: `1px solid ${t.border}`,
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            background: t.footerBg,
            flexShrink: 0,
          }}
        >
          <button
            onClick={handleClose}
            style={{
              padding: "8px 20px",
              border: `1.5px solid ${t.cancelBorder}`,
              borderRadius: 7,
              background: t.cancelBg,
              color: t.cancelText,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 500,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = isDark ? "#94a3b8" : "#9ca3af"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = t.cancelBorder; }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: "8px 22px",
              border: "none",
              borderRadius: 7,
              background: selected.size > 0 ? "#0064d9" : "#93c5fd",
              color: "#fff",
              cursor: selected.size > 0 ? "pointer" : "default",
              fontSize: 13,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            Save
            {selected.size > 0 && (
              <span
                style={{
                  background: "rgba(255,255,255,0.28)",
                  borderRadius: 10,
                  padding: "1px 7px",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {selected.size}
              </span>
            )}
          </button>
        </div>
      </div>

      <style>{`@keyframes dlg-spin { to { transform: rotate(360deg); } }`}</style>
    </>,
    document.body
  );
}

const DialogInitFlowTemplate = (props) => {
  const { rows, requesting } = useSelector((state) => state.Process);
  const dispatch = useDispatch();

  if (!props.status || props.id < 0) return null;

  return (
    <AssignPanel
      onClose={props.close}
      id={props.id}
      itemType={props.itemType}
      componentName={props.componentName}
      header={props.header}
      selectedRecord={props.selectedRecord}
      rows={rows}
      requesting={requesting}
      dispatch={dispatch}
    />
  );
};

DialogInitFlowTemplate.propTypes = {};

export default DialogInitFlowTemplate;
